import { notBlankTextRequirements } from "@/app/(core)/register/types";
import {
  Button,
  Stack,
  TextInput,
  Textarea,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { Session } from "next-auth";
import { useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { handleResponseError } from "../../../../../../../../lib/strapi/strapiApi";
import { FormType } from "../../../../../../../api/strapi/sendCustomerEmail/route";
import { FormCompleteMsg } from "../../../../../../../components/footer/components/ContactForm";
import { setNotification } from "../../../../../../../components/notifications";

export default function ReportForm({
  questionDocumentId,
  formId,
  session,
}: {
  questionDocumentId: string;
  formId: string;
  session: Session | null;
}) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const theme = useMantineTheme();
  const form = useForm({
    initialValues: {
      questionDocumentId: questionDocumentId,
      name: session ? session.user.name : "",
      email: session ? session.user.email : "",
      message: "",
    },
    validate: (values) => {
      return {
        name: notBlankTextRequirements.test(values.name)
          ? null
          : "Enter a valid name",
        email:
          /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/.test(
            values.email
          )
            ? null
            : "Invalid email",
        message: notBlankTextRequirements.test(values.message)
          ? null
          : "Enter a valid message",
      };
    },
    validateInputOnChange: ["message", "email", "name"],
    transformValues: (
      values
    ): {
      name: string;
      email: string;
      message: string;
      formType: FormType;
    } => ({
      name: values.name,
      email: values.email,
      message: `I believe i have found an improvement with question ${values.questionDocumentId}. Specifically: ${values.message}`,
      formType: "feedback",
    }),
  });
  async function sendEmail() {
    setLoading(true);
    // get capatcha key
    setNotification(
      "Suggestion",
      false,
      "Checking you're not a robot...",
      "success",
      true
    );
    if (!executeRecaptcha) {
      setLoading(false);
      setNotification(
        "Suggestion",
        false,
        "Our system was not able to verify your human status. Please check you are connected to the internet. If so, try again now!",
        "error"
      );
      return;
    }

    const captchaToken = await executeRecaptcha("QuestionReportSubmit");
    const res = await fetch("/api/strapi/sendCustomerEmail", {
      method: "POST",
      body: JSON.stringify({ ...form.getTransformedValues(), captchaToken }),
    });
    const response = await res.json();
    if (!res.ok) {
      setNotification(
        "Suggestion",
        false,
        handleResponseError(response),
        "error"
      );
      setLoading(false);
      return;
    }
    setNotification("Suggestion", true, response.msg, "success");
    setLoading(false);
    setSent(true);
    setTimeout(() => {
      modals.close(formId);
    }, 1200);
  }
  return (
    <>
      {sent ? (
        <FormCompleteMsg theme={theme} />
      ) : (
        <Stack>
          <TextInput
            disabled
            withAsterisk
            label="Question ID"
            placeholder="Question ID"
            {...form.getInputProps("questionDocumentId")}
          />
          <TextInput
            withAsterisk
            label="Name"
            placeholder="Name"
            {...form.getInputProps("name")}
          />
          <TextInput
            withAsterisk
            label="Email"
            placeholder="Where we should reply to your improvement"
            {...form.getInputProps("email")}
          />
          <Textarea
            autosize
            minRows={4}
            maxRows={10}
            withAsterisk
            label="Improvement"
            placeholder="A brief suggestion of how the question can be improved."
            {...form.getInputProps("message")}
          />
          <Button
            name="Send"
            color="darkblue"
            mt={"3rem"}
            loading={loading}
            disabled={!form.isValid()}
            ml={"auto"}
            mr={0}
            onClick={() => sendEmail()}
          >
            Send
          </Button>
        </Stack>
      )}
    </>
  );
}
